/**
 * CAMPAIGN W1.4 — the radio comms panel.
 *
 * Renders a CommsScript line-by-line in the lower third: `CALLSIGN > text`,
 * typed glyph-by-glyph on the client dt beat-clock (same approach as the
 * ledger reveal). Enter/click completes the current line, then advances.
 * Choices render as [1]/[2] rows (click or the 1/2 keys). DIEGETIC: the sim
 * keeps running while radio plays — main.ts only gates the pip keys and frees
 * the pointer while a choice is pending.
 */
import './commsPanel.css';
import type { CommsScript } from '@blackmarble/sim-core';
import { voiceAssetFor } from '../audio/audioCatalog.js';

const CHARS_PER_SEC = 28; // radio cadence — a touch quicker than the ledger's carve
// When pacing the reveal to a clip's duration, keep it within a readable band so a
// very long or very short clip never crawls or blurs past legibility.
const MIN_REVEAL_CPS = 6;
const MAX_REVEAL_CPS = 90;

/**
 * CAMPAIGN VO: the slice of AudioEngine the panel needs to play voice-over — the
 * live context + the dialogue input node (both null until the audio context is
 * resumed on a user gesture). The input routes through the radio chain (band-pass
 * + compression) when RADIO FILTER is on. Read lazily at play time so VO works
 * once audio is up.
 */
export interface CommsVoiceSink {
  readonly audioContext: AudioContext | null;
  readonly dialogueInput: AudioNode | null;
}

export class CommsPanel {
  private readonly root: HTMLDivElement;
  private script: CommsScript | null = null;
  private lineIdx = 0;
  private reveal = 0; // glyphs revealed of the current line
  private onDone: ((commsId: string) => void) | null = null;
  private onChoice: ((commsId: string, key: '1' | '2') => void) | null = null;
  private inChoices = false;
  // CAMPAIGN VO: decoded-clip cache + the currently-playing source. `voiceToken`
  // guards against a stale async decode resolving after the line moved on.
  private readonly voiceBuffers = new Map<string, AudioBuffer | null>();
  private currentVoice: AudioBufferSourceNode | null = null;
  private voiceToken = 0;
  // Reveal cadence for the current line — paced to its clip duration once decoded.
  private lineCharsPerSec = CHARS_PER_SEC;

  constructor(
    private readonly voice: CommsVoiceSink | null = null,
    /** Accessibility: when true, comms advance on VO clip end instead of on input. */
    private readonly autoAdvance: () => boolean = () => false,
  ) {
    this.root = document.createElement('div');
    this.root.id = 'comms-panel';
    this.root.style.display = 'none';
    document.body.appendChild(this.root);

    this.root.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('[data-choice]') as HTMLElement | null;
      if (btn) this.choose(btn.getAttribute('data-choice') as '1' | '2');
      else this.advance();
    });
    window.addEventListener('keydown', (e) => {
      if (!this.script) return;
      const k = e.key;
      if (this.inChoices && (k === '1' || k === '2')) this.choose(k);
      else if (!this.inChoices && (k === 'Enter' || k === ' ')) this.advance();
    });
  }

  get isOpen(): boolean {
    return this.script !== null;
  }
  /** True while [1]/[2] rows are on screen (main.ts gates pips + frees pointer). */
  get choicesVisible(): boolean {
    return this.inChoices;
  }

  /** Begin a script. Replaces any script already playing. */
  play(
    script: CommsScript,
    onDone: (commsId: string) => void,
    onChoice: (commsId: string, key: '1' | '2') => void,
  ): void {
    this.script = script;
    this.onDone = onDone;
    this.onChoice = onChoice;
    this.lineIdx = 0;
    this.reveal = 0;
    this.inChoices = false;
    this.root.style.display = 'block';
    this.render();
    this.playLineVoice(); // VO for the opening line, synced to its reveal start
  }

  hide(): void {
    this.stopVoice();
    this.script = null;
    this.inChoices = false;
    this.root.style.display = 'none';
    this.root.innerHTML = '';
  }

  /** Advance the glyph clock (called once per frame with client dt). */
  tick(dt: number): void {
    if (!this.script || this.inChoices) return;
    const line = this.script.lines[this.lineIdx];
    if (!line) return;
    if (this.reveal < line.text.length) {
      this.reveal = Math.min(line.text.length, this.reveal + dt * this.lineCharsPerSec);
      this.render();
    }
  }

  /** Enter/click: complete the typing line, else move to the next line/choices. */
  private advance(): void {
    if (!this.script) return;
    const line = this.script.lines[this.lineIdx];
    if (line && this.reveal < line.text.length) {
      this.reveal = line.text.length;
      this.stopVoice(); // skipping the reveal cuts its clip short
      this.render();
      return;
    }
    if (this.lineIdx < this.script.lines.length - 1) {
      this.lineIdx++;
      this.reveal = 0;
      this.render();
      this.playLineVoice(); // next line begins revealing — start its clip
      return;
    }
    // Last line is fully shown: choices, or done.
    if (this.script.choices && this.script.choices.length > 0) {
      if (!this.inChoices) {
        this.inChoices = true;
        this.stopVoice();
        document.exitPointerLock?.(); // the choice rows must be clickable
        this.render();
      }
      return;
    }
    const id = this.script.id;
    this.hide();
    this.onDone?.(id);
  }

  private choose(key: '1' | '2'): void {
    if (!this.script || !this.inChoices) return;
    const id = this.script.id;
    this.hide();
    this.onChoice?.(id, key);
  }

  /** Re-render the visible lines (typed line last) + choice rows. */
  private render(): void {
    if (!this.script) return;
    const shown = this.script.lines.slice(0, this.lineIdx + 1).map((l, i) => {
      const text =
        i < this.lineIdx ? l.text : l.text.slice(0, Math.floor(this.reveal));
      return (
        `<div class="cp-line"><span class="cp-speaker">${l.speaker} &gt;</span> ` +
        `<span class="cp-text">${text}</span></div>`
      );
    });
    const line = this.script.lines[this.lineIdx];
    const typed = line ? this.reveal >= line.text.length : true;
    const last = this.lineIdx >= this.script.lines.length - 1;
    let footer = '';
    if (this.inChoices && this.script.choices) {
      footer =
        `<div class="cp-choices">` +
        this.script.choices
          .map((c) => `<button class="cp-choice" data-choice="${c.key}">[${c.key}] ${c.label}</button>`)
          .join('') +
        `</div>`;
    } else if (typed) {
      footer = `<div class="cp-advance">${last && this.script.choices?.length ? 'RESPOND [ENTER]' : last ? 'CLOSE [ENTER]' : 'CONTINUE [ENTER]'}</div>`;
    }
    this.root.innerHTML = shown.join('') + footer;
  }

  /** CAMPAIGN VO: start the current line's voice clip (if it has a cast voice and
   * the audio context is up). Stops any clip already playing first. Cast lines
   * with no generated file, or a decode error, degrade silently to text-only. */
  private playLineVoice(): void {
    this.stopVoice();
    this.lineCharsPerSec = CHARS_PER_SEC; // default until a clip's duration is known
    if (!this.script || !this.voice) return;
    const line = this.script.lines[this.lineIdx];
    if (!line) return;
    const url = voiceAssetFor(this.script.id, this.lineIdx, line.speaker, line.voice);
    if (url) void this.startVoiceClip(url);
  }

  /** Fetch + decode (cached) + play `url` on the dialogue input (radio chain). A
   * `voiceToken` snapshot ensures a clip that finishes decoding after the line
   * advanced (or was stopped) is dropped rather than played late. When a buffer is
   * present the typed reveal is paced to its duration; with auto-advance on, the
   * clip's natural end steps to the next line. */
  private async startVoiceClip(url: string): Promise<void> {
    const ctx = this.voice?.audioContext;
    const bus = this.voice?.dialogueInput;
    if (!ctx || !bus) return;
    const token = ++this.voiceToken;
    let buffer = this.voiceBuffers.get(url);
    if (buffer === undefined) {
      buffer = await this.decodeVoice(ctx, url);
      this.voiceBuffers.set(url, buffer);
    }
    if (token !== this.voiceToken || !buffer) return; // line moved on, or no clip
    this.paceRevealToClip(buffer.duration);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(bus);
    source.onended = (): void => this.onVoiceEnded(source);
    source.start();
    this.currentVoice = source;
  }

  /** Pace the current line's reveal so it finishes about when its clip does. */
  private paceRevealToClip(durationSec: number): void {
    const line = this.script?.lines[this.lineIdx];
    if (!line || line.text.length === 0 || durationSec <= 0) return;
    const cps = line.text.length / durationSec;
    this.lineCharsPerSec = Math.max(MIN_REVEAL_CPS, Math.min(MAX_REVEAL_CPS, cps));
  }

  /** A VO clip finished on its own. Clear it, and (accessibility) auto-advance the
   * line when enabled — a manual stop nulls currentVoice first, so it is ignored. */
  private onVoiceEnded(source: AudioBufferSourceNode): void {
    if (this.currentVoice !== source) return;
    this.currentVoice = null;
    if (!this.autoAdvance() || this.inChoices || !this.script) return;
    const line = this.script.lines[this.lineIdx];
    if (line) this.reveal = line.text.length; // ensure the line is fully shown first
    this.advance();
  }

  /** Decode a VO file, returning null on 404 / decode error (silent fallback). */
  private async decodeVoice(ctx: AudioContext, url: string): Promise<AudioBuffer | null> {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      return await ctx.decodeAudioData(await response.arrayBuffer());
    } catch {
      return null;
    }
  }

  /** Stop the current VO clip and invalidate any in-flight decode. */
  private stopVoice(): void {
    this.voiceToken++;
    const source = this.currentVoice;
    this.currentVoice = null;
    if (!source) return;
    try {
      source.stop();
    } catch {
      /* already stopped/ended */
    }
    source.disconnect();
  }
}
